import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ILinks, IMeta, IRequestOptions } from './../../../app/@store/request-options.interface';
import { Account } from './../../../app/@shared/models/account.model';
import { HttpErrorResponse } from '@angular/common/http';
import { createReducer, on } from '@ngrx/store';
import * as AccountActions from './account.actions';
import * as _ from 'lodash';
import { ColumnSortDirection } from '@gridscale/ingrid/components/data-table';
import { AccountProjectRelation, AccountsResponse } from './../../../app/@shared/models/accounts_response.model';
import { AccountUsersResponse } from './../../../app/@shared/models/account_users_response.model';
import { AccountFeatureFlagsResponse } from './../../../app/@shared/models/account_feature_flags_response.model';
import { AccountResponse } from './../../../app/@shared/models/account_response.model';
import { contractmanagement_api } from '@gridscale/gs-services/contractmanagement_api'; import { PaymentMethodsWhitelistResponseData } from '../../@shared/models/paymentMethod.model';

export const accountFeatureKey = 'accounts';

export interface AccountStoreEntry extends Partial<AccountResponse['contract']> {
  management_data: Partial<contractmanagement_api.ContractDetailAttributes>;
  details: Partial<contractmanagement_api.ContractDetailsAttributes>;
  billing_address: contractmanagement_api.BillingAddressAttributes | null;
  payment_methods_whitelist: PaymentMethodsWhitelistResponseData[]
}


export const adapter: EntityAdapter<Partial<AccountStoreEntry>> = createEntityAdapter<Partial<AccountStoreEntry>>({
  selectId: (a => a.object_uuid!),
  sortComparer: false
});

export const listAdapter: EntityAdapter<Partial<AccountsResponse['contracts'][0]>> = createEntityAdapter<Partial<AccountsResponse['contracts'][0]>>({
  selectId: (a => a.object_uuid!),
  sortComparer: false
});

const listAdapterInitialState = listAdapter.getInitialState({
  listConfig: {
    size: 50,
    //sort: 'name',
    //fields: ['object_uuid', 'name', 'payment_enabled', 'locked', 'disconnected', 'change_time', 'create_time', 'feature_flags', 'prices', 'last_billing_time', 'resource_limits', 'relations', 'favorite'],
    page: 0
  },
  loaded: false,
  loading: false,
  loadingNext: false,
  loadingTotal: false,
  loadedTotal: false,
  search: ''
}) as ListState;


const favsAdapterInitialState = listAdapter.getInitialState({
  loaded: false,
  loading: false
}) as FavsState;

export interface ListState extends EntityState<Partial<Partial<AccountsResponse['contracts'][0]>>> {
  listConfig: IRequestOptions;
  loaded: boolean;
  loading: boolean;
  loadingNext: boolean;
  loadingTotal: boolean;
  loadedTotal: boolean;
  meta?: IMeta;
  links?: ILinks<Account>;
  error?: Error | HttpErrorResponse | undefined;
}

export interface FavsState extends EntityState<Partial<Partial<AccountsResponse['contracts'][0]>>> {
  loaded: boolean;
  loading: boolean;
  error?: Error | HttpErrorResponse | undefined;
}

export interface State extends EntityState<Partial<AccountStoreEntry>> {
  loading: { [key: string]: boolean };
  loaded: { [key: string]: boolean };
  patching: { [key: string]: boolean };
  deleting: { [key: string]: boolean | undefined };
  error?: { [key: string]: Error | HttpErrorResponse | undefined };
  accountRelations: { [key: string]: AccountsResponse['contracts'][0] };
  accountProjects: { [key: string]: AccountProjectRelation };
  accountProjectsCounter: number;
  accountProjectsLoaded: boolean;
  user_relations: Record<string, {
    loading: boolean;
    loaded: boolean;
    data: AccountUsersResponse['user_relations'] | undefined;
  }>;
  account_feature_flags: Record<string, {
    loading: boolean;
    loaded: boolean;
    data: AccountFeatureFlagsResponse['feature_flags']
  }>;
  list: ListState;
  favs: FavsState;
  total: number;
}

export const initialState: State = adapter.getInitialState({
  loading: {},
  loaded: {},
  patching: {},
  deleting: {},
  accountRelations: {},
  accountProjects: {},
  accountProjectsCounter: 0,
  accountProjectsLoaded: false,
  user_relations: {},
  account_feature_flags: {},
  list: listAdapterInitialState,
  favs: favsAdapterInitialState,
  total: 0
});

export const reducer = createReducer(
  initialState,

  on(AccountActions.setListConfig, (state, action) => ({
    ...state,

    list: listAdapter.removeAll({
      ...state.list,
      loaded: false,
      listConfig: {
        ...action.config
      }
    })
  })),

  on(AccountActions.loadAccounts, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: true
    }
  })),

  on(AccountActions.loadAccountsSuccess, (state: State, action) => ({
    ...state,

    list: listAdapter.setAll(action.data, {
      ...state.list,
      meta: action.meta,
      loading: false,
      loaded: true,
      error: undefined
    })
  })),

  on(AccountActions.loadAccountsFailure, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      entities: {},
      loading: false,
      loaded: true,
      error: action.error
    }
  })),

  on(AccountActions.loadAccountsTotal, (state, action) => ({
    ...state,
    loadingTotal: true
  })),

  on(AccountActions.loadAccountsTotalSuccess, (state: State, action) => ({
    ...state,
    total: action.total,
    loadingTotal: false,
    loadedTotal: true
  })),

  on(AccountActions.loadAccountsNext, (state, action) => ({
    ...state,

    list: {
      ...state.list as ListState,
      loading: true,
      loadingNext: true
    }
  })),

  on(AccountActions.loadAccountsNextSuccess, (state: State, action) => ({
    ...state,

    list: listAdapter.upsertMany(action.data, {
      ...state.list,
      meta: action.meta,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: undefined
    })
  })),

  on(AccountActions.loadAccountsNextFailure, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      loading: false,
      loadingNext: false,
      loaded: true,
      error: action.error
    }
  })),



  on(AccountActions.loadFavAccounts, (state, action) => ({
    ...state,

    favs: {
      ...state.favs,
      loading: true
    }
  })),

  on(AccountActions.loadFavAccountsSuccess, (state: State, action) => ({
    ...state,

    favs: listAdapter.setAll(action.data, {
      ...state.favs,
      loading: false,
      loaded: true,
      error: undefined
    })
  })),

  on(AccountActions.loadFavAccountsFailure, (state, action) => ({
    ...state,

    favs: {
      ...state.list,
      entities: {},
      loading: false,
      loaded: true,
      error: action.error
    }
  })),




  on(AccountActions.loadAccount, (state, action) => ({
    ...state,

    loading: { ...state.loading, [action.account_uuid]: true },
    loaded: { ...state.loading, [action.account_uuid]: false }
  })),

  on(AccountActions.loadAccountSuccess, (state, action) =>
    adapter.setOne(
      { ...action.data, management_data: action.management_data, billing_address: action.billing_address, details: action.details, payment_methods_whitelist: action.payment_methods_whitelist },
      {
        ...state,

        error: { ...(state.error || {}), [action.account_uuid]: undefined },
        loading: { ...state.loading, [action.account_uuid]: false },
        loaded: { ...state.loaded, [action.account_uuid]: true },
        list: listAdapter.setOne(
          //{ ..._.omit(action.data, 'relations') },
          { ...action.data },
          {
            ...state.list,
            loaded: true,
            error: undefined,
            loading: false,
            loadingNext: false
          }
        )
      }
    )
  ),

  on(AccountActions.loadAccountFailure, (state, action) => ({
    ...state,

    error: { ...state.error, [action.account_uuid]: action.error },
    loading: { ...state.loading, [action.account_uuid]: false },
    loaded: { ...state.loaded, [action.account_uuid]: true }
  })),



  on(AccountActions.loadAccountOnly, (state, action) => ({
    ...state,

    loading: { ...state.loading, [action.account_uuid]: true },
    loaded: { ...state.loading, [action.account_uuid]: false }
  })),

  on(AccountActions.loadAccountOnlySuccess, (state, action) =>
    adapter.upsertOne(
      { ...action.data },
      {
        ...state,

        error: { ...(state.error || {}), [action.account_uuid]: undefined },
        loading: { ...state.loading, [action.account_uuid]: false },
        loaded: { ...state.loaded, [action.account_uuid]: true },
        list: listAdapter.upsertOne(
          //{ ..._.omit(action.data, 'relations') },
          { ...action.data },
          {
            ...state.list,
            loaded: true,
            error: undefined,
            loading: false,
            loadingNext: false
          }
        )
      }
    )
  ),

  on(AccountActions.loadAccountOnlyFailure, (state, action) => ({
    ...state,

    error: { ...state.error, [action.account_uuid]: action.error },
    loading: { ...state.loading, [action.account_uuid]: false },
    loaded: { ...state.loaded, [action.account_uuid]: true }
  })),




  on(AccountActions.patchAccountSuccess, (state, action) => state),

  on(AccountActions.patchBillingAddressSuccess, (state, action) => state),

  // Load all Account Projects!
  on(AccountActions.loadAllAccountsProjectsSuccess, (state, action) => ({
    ...state,
    accountProjectsCounter: action.count
  })),

  on(AccountActions.loadAllAccountsProjectsSingleCallSuccess, (state, action) => {
    const accounts: Record<string, AccountsResponse['contracts'][0]> = {};
    action.result.forEach(account => {
      if (account) {
        accounts[account.object_uuid] = account;
      }
    });

    const projects: Record<string, AccountProjectRelation & { account_uuid: string; contract_name: string; }> = {};
    action.result.forEach(contract => {
      contract?.relations?.projects?.forEach(project => {
        projects[project.object_uuid] = { ...project, account_uuid: contract.object_uuid, contract_name: contract.name };
      });
    });

    let accountProjectLoaded = state.accountProjectsLoaded;
    if (state.accountProjectsCounter - 1 === 0) {
      accountProjectLoaded = true;
    }

    return {
      ...state,
      accountProjectsCounter: state.accountProjectsCounter - 1,
      accountProjectsLoaded: accountProjectLoaded,
      accountRelations: {
        ...state.accountRelations,
        ...accounts
      },
      accountProjects: {
        ...state.accountProjects,
        ...projects
      }
    };
  }),

  on(AccountActions.deleteAccount, (state, action) => ({
    ...state,

    deleting: { ...state.deleting, [action.account_uuid]: true },
    error: { ...state.error, [action.account_uuid]: undefined }
  })),

  on(AccountActions.deleteAccountSuccess, (state, action) => ({
    ...state,

    deleting: { ...state.deleting, [action.account_uuid]: undefined },
    error: { ...state.error, [action.account_uuid]: undefined },

    list: listAdapter.removeOne(action.account_uuid, {
      ...state.list,

      meta: {
        ...state.list.meta!,
        count: state.list.meta!.count - 1,
        total: state.list.meta!.total - 1
      }
    })
  })),

  on(AccountActions.deleteAccountFailure, (state, action) => ({
    ...state,

    deleting: { ...state.deleting, [action.account_uuid]: false },
    error: { ...state.error, [action.account_uuid]: action.error }
  })),

  on(AccountActions.tableChangeSort, (state, action) => {
    const sort = (action.newSort.direction === ColumnSortDirection.ASC ? '+' : '-') + action.newSort.columnId;

    const tmpstate = {
      ...state,
      list: {
        ...state.list,
        listConfig: {
          ...state.list.listConfig,
          sort
        }
      }
    };

    return tmpstate;
  }),

  on(AccountActions.setSearch, (state, action) => ({
    ...state,

    list: {
      ...state.list,
      search: action.search
    }
  })),

  on(AccountActions.toggleFavoriteSuccess, (state, action) =>
    adapter.updateOne(
      { id: action.account_uuid, changes: { favorite: action.favorite } },
      {
        ...state,
      }
    )
  ),

  on(AccountActions.loadAccountUsers, (state, action) => ({
    ...state,

    user_relations: {
      ...state.user_relations,
      [action.account_uuid]: {
        loaded: _.get(state, ['user_relations', action.account_uuid, 'loaded'], false),
        loading: true,
        data: _.get(state, ['user_relations', action.account_uuid, 'data'], undefined),
      }
    }
  })),

  on(AccountActions.loadAccountUsersSuccess, (state, action) => ({
    ...state,

    user_relations: {
      ...state.user_relations,
      [action.account_uuid]: {
        loading: false,
        loaded: true,
        data: action.data
      }
    }
  })),

  on(AccountActions.loadAccountUsersFailure, (state, action) => ({
    ...state,

    user_relations: {
      ...state.user_relations,
      [action.account_uuid]: {
        loaded: false,
        loading: false,
        data: _.get(state, ['user_relations', action.account_uuid, 'data'], []),
      }
    }

  })),

  on(AccountActions.patchAccountUserSuccess, (state, action) => state),

  on(AccountActions.loadAccountFeatureFlag, (state, action) => ({
    ...state,

    account_feature_flags: {
      ...(state.account_feature_flags || {}),
      [action.account_uuid]: {
        loading: true,
        loaded: _.get(state, ['account_feature_flags', action.account_uuid, 'loaded'], false),
        data: _.get(state, ['account_feature_flags', action.account_uuid, 'data'], [])
      }

    }
  })),

  on(AccountActions.loadAccountFeatureFlagSuccess, (state, action) => ({
    ...state,

    account_feature_flags: {
      ...(state.account_feature_flags || {}),
      [action.account_uuid]: {
        loading: false,
        loaded: true,
        data: (action?.data || [])
      }
    }
  })),

  on(AccountActions.loadAccountFeatureFlagFailure, (state, action) => ({
    ...state,

    account_feature_flags: {
      ...(state.account_feature_flags || {}),
      [action.account_uuid]: {
        loading: false,
        loaded: false,
        data: _.get(state, ['account_feature_flags', action.account_uuid, 'data'], [])
      }
    }
  })),
);
